'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FullscreenState } from '@/hooks/use-fullscreen'
import { PRODUCT_EVENTS, captureProductEvent, type SantaTrackerCta } from '@/lib/product-analytics'
import type { TrackerCopy, TrackerLocale, TrackerState } from '@/lib/santa-tracker'
import type { TrackerSelection } from './tracker-detail-panel'
import { useTrackerShare, type ShareMethod } from './use-tracker-share'

/**
 * Lo que el visitante puede hacer en el tracker, con su medición.
 *
 * Se mide **solo la interacción deliberada**: abrir una ciudad, abrir el
 * trineo, compartir, encender el sonido, ir a pantalla completa y pulsar un
 * CTA. Ni el tic del reloj ni el avance del trineo emiten nada — serían cientos
 * de eventos por visita que no responden a ninguna pregunta.
 */

/** Toques en el Polo Norte que despiertan la aurora. */
const POLE_TAPS_FOR_SURPRISE = 5

type TrackerEvent = (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS]

export interface TrackerInteractions {
  selection: TrackerSelection
  soundOn: boolean
  shareFeedback: boolean
  auroraAwake: boolean
  selectStop: (stopId: string) => void
  selectSleigh: () => void
  clearSelection: () => void
  toggleSound: () => void
  toggleFullscreen: () => void
  share: () => void
  tapNorthPole: () => void
  trackCtaClick: (cta: SantaTrackerCta) => void
}

/**
 * Emisor de eventos con la fase y el idioma ya puestos, más la visita inicial
 * (una sola por sesión de página).
 */
function useTrackerTracking(phase: TrackerState['phase'], locale: TrackerLocale) {
  const track = useCallback(
    (event: TrackerEvent, extra?: Record<string, string>) =>
      captureProductEvent(event, { phase, locale, ...extra }),
    [locale, phase]
  )

  // La visita se emite con la fase del **primer** render. Se congela con
  // `useState` (no con un ref, que no se puede leer en render) para que el
  // efecto no dependa de un valor que cambia cada segundo.
  const [initialPhase] = useState(phase)
  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current) return
    viewedRef.current = true
    captureProductEvent(PRODUCT_EVENTS.SANTA_TRACKER_VIEWED, { phase: initialPhase, locale })
  }, [initialPhase, locale])

  return track
}

export function useTrackerInteractions({
  copy,
  locale,
  state,
  shareUrl,
  fullscreen,
}: {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  shareUrl: string
  fullscreen: FullscreenState
}): TrackerInteractions {
  const [selection, setSelection] = useState<TrackerSelection>(null)
  const [soundOn, setSoundOn] = useState(false)
  const [poleTaps, setPoleTaps] = useState(0)

  const { phase } = state
  const track = useTrackerTracking(phase, locale)

  const onShared = useCallback(
    (method: ShareMethod) => track(PRODUCT_EVENTS.SANTA_TRACKER_SHARED, { method }),
    [track]
  )
  const { feedback: shareFeedback, share } = useTrackerShare({
    title: copy.share.title,
    text: copy.share.text[phase],
    url: shareUrl,
    onShared,
  })

  const selectStop = useCallback(
    (stopId: string) => {
      setSelection({ kind: 'stop', stopId })
      track(PRODUCT_EVENTS.SANTA_TRACKER_CITY_OPENED, { stop_id: stopId })
    },
    [track]
  )

  const selectSleigh = useCallback(() => {
    setSelection({ kind: 'sleigh' })
    track(PRODUCT_EVENTS.SANTA_TRACKER_SLEIGH_OPENED)
  }, [track])

  // El evento va FUERA del `setState`: React puede reejecutar un actualizador
  // (StrictMode lo hace siempre), y dentro se emitiría por duplicado.
  const toggleSound = useCallback(() => {
    const next = !soundOn
    setSoundOn(next)
    if (next) track(PRODUCT_EVENTS.SANTA_TRACKER_SOUND_ENABLED)
  }, [soundOn, track])

  const { isFullscreen, toggleFullscreen: toggleNativeFullscreen } = fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) track(PRODUCT_EVENTS.SANTA_TRACKER_FULLSCREEN_ENABLED)
    void toggleNativeFullscreen()
  }, [isFullscreen, track, toggleNativeFullscreen])

  const trackCtaClick = useCallback(
    (cta: SantaTrackerCta) => track(PRODUCT_EVENTS.SANTA_TRACKER_CTA_CLICKED, { cta }),
    [track]
  )

  return {
    selection,
    soundOn,
    shareFeedback,
    auroraAwake: poleTaps >= POLE_TAPS_FOR_SURPRISE,
    selectStop,
    selectSleigh,
    clearSelection: useCallback(() => setSelection(null), []),
    toggleSound,
    toggleFullscreen,
    share,
    tapNorthPole: useCallback(() => setPoleTaps((taps) => taps + 1), []),
    trackCtaClick,
  }
}
